import React from "react";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { Link, useParams, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import dayjs from "dayjs";
import { useStudent } from "./hooks/useStudent";
import { showToast } from "../../../core/utils/alert";
import { useGetStudentByIdQuery } from "./api/studentApi";
import { API_CONFIG } from "../../../core/config";

interface StudentModalsProps {
  StudentId?: string;
  studentData?: any;
  showDeleteModal?: boolean;
  setShowDeleteModal?: (show: boolean) => void;
  showDeactivateModal?: boolean;
  setShowDeactivateModal?: (show: boolean) => void;
  showActivateModal?: boolean;
  setShowActivateModal?: (show: boolean) => void;
  isDeactivateFromDelete?: boolean;
}

const StudentModals: React.FC<StudentModalsProps> = ({
  StudentId,
  studentData,
  showDeleteModal,
  setShowDeleteModal,
  showDeactivateModal,
  setShowDeactivateModal,
  showActivateModal,
  setShowActivateModal,
  isDeactivateFromDelete = false,
}) => {
  const navigate = useNavigate();
  const routes = all_routes;
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0"); // Month is zero-based, so we add 1
  const day = String(today.getDate()).padStart(2, "0");
  const formattedDate = `${month}-${day}-${year}`;
  const defaultValue = dayjs(formattedDate);

  const { deleteStudent, addNewStudent, deactivateStudent, activateStudent } =
    useStudent();
  const { id } = useParams<{ id: string }>();
  const { data: studentResponse } = useGetStudentByIdQuery(
    { id: id || "" },
    { skip: !id }
  );
  const student = studentResponse?.data?.student;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const response = await addNewStudent(studentData);

    if (response.data?.student) {
      showToast({
        title: "Success",
        text: "Student created successfully",
        icon: "success",
      });
      navigate(routes.studentList);
    } else {
      showToast({
        title: "Error",
        text: response.error || "Student creation failed",
        icon: "error",
      });
    }
  };
  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    const response = await addNewStudent(studentData);

    if (response.data?.student) {
      showToast({
        title: "Success",
        text: "Student navigated successfully to documents",
        icon: "success",
      });
      navigate(
        routes.studentDocuments.replace(
          ":id",
          response.data.student.id.toString()
        )
      );
    } else {
      showToast({
        title: "Error",
        text: response.error || "Student creation failed",
        icon: "error",
      });
    }
  };

  const handleDelete = async () => {
    if (StudentId) {
      const result: any = await deleteStudent(StudentId);

      if (result.success) {
        setShowDeleteModal?.(false);
        showToast({
          title: "Success",
          text: result.message || "Record deleted successfully",
          icon: "success",
        });
      } else if (result.status === 409) {
        setShowDeleteModal?.(false);
        setShowDeactivateModal?.(true);
      } else {
        showToast({
          title: "Error",
          text: result.error || "Could not delete the record",
          icon: "error",
        });
      }
    }
  };

  const handleActivate = async () => {
    if (StudentId) {
      const result: any = await activateStudent(StudentId);

      if (result.success) {
        showToast({
          title: "Success",
          text: "Student activated successfully",
          icon: "success",
        });
        setShowActivateModal?.(false);
      } else {
        showToast({
          title: "Error",
          text: result.error || "Could not activate the student",
          icon: "error",
        });
      }
    }
  };
  const handleDeactivate = async () => {
    if (StudentId) {
      const result: any = await deactivateStudent(StudentId);

      if (result.success) {
        showToast({
          title: "Success",
          text: "Student deactivated successfully",
          icon: "success",
        });
        setShowDeactivateModal?.(false);
      } else {
        showToast({
          title: "Error",
          text: result.error || "Could not deactivate the student",
          icon: "error",
        });
      }
    }
  };
  return (
    <>
      {/* Delete Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          id="delete-modal"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleDelete();
                }}
              >
                <div className="modal-body text-center">
                  <span className="delete-icon">
                    <i className="ti ti-trash" />
                  </span>
                  <h4>Confirm Deletion</h4>
                  <p>
                    You want to delete the record, this can't be undone once you
                    delete.
                  </p>
                  <div className="d-flex justify-content-center">
                    <button
                      type="button"
                      className="btn btn-light me-3"
                      onClick={() => setShowDeleteModal?.(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-danger">
                      Yes, Delete
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Deactivation Modal */}
      {showDeactivateModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          id="deactivate-modal"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleDeactivate();
                }}
              >
                <div className="modal-body text-center p-4">
                  <div className="warning-icon mb-4">
                    <i
                      className="ti ti-alert-triangle"
                      style={{ fontSize: "4rem", color: "#ffc107" }}
                    />
                  </div>
                  <h4 className="mb-3 fw-semibold">
                    {isDeactivateFromDelete
                      ? "Associated Records Found"
                      : "Confirm Deactivation"}
                  </h4>
                  <p className="text-muted mb-4">
                    {isDeactivateFromDelete
                      ? "This student has associated records that prevent deletion. Would you like to deactivate the student instead?"
                      : "Are you sure you want to deactivate this student? The student's data will be preserved but they won't be able to access the system."}
                  </p>
                  <div className="d-flex justify-content-center gap-3">
                    <button
                      type="button"
                      className="btn btn-light px-4"
                      onClick={() => {
                        setShowDeactivateModal?.(false);
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-warning px-4">
                      Yes, Deactivate
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Activation Modal */}
      {showActivateModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          id="activate-modal"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleActivate();
                }}
              >
                <div className="modal-body text-center p-4">
                  <div className="success-icon mb-4">
                    <i
                      className="ti ti-user-check"
                      style={{ fontSize: "4rem", color: "#198754" }}
                    />
                  </div>
                  <h4 className="mb-3 fw-semibold">Confirm Activation</h4>
                  <p className="text-muted mb-4">
                    Are you sure you want to activate this student? The student
                    will regain access to the system.
                  </p>
                  <div className="d-flex justify-content-center gap-3">
                    <button
                      type="button"
                      className="btn btn-light px-4"
                      onClick={() => setShowActivateModal?.(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-success px-4">
                      Yes, Activate
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add modal backdrop when any modal is shown */}
      {(showDeleteModal || showDeactivateModal || showActivateModal) && (
        <div className="modal-backdrop fade show"></div>
      )}

      {/* Login Details */}
      <div className="modal fade" id="login_detail">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Login Details</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body">
              <div className="student-detail-info">
                <span className="student-img">
                  {student?.docs?.photo ? (
                    <img
                      src={`${API_CONFIG.BASE_URL}students/get-thumbnail/${student.docs.photo
                        }?v=${new Date().getTime()}`}
                      alt="Student"
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        const target = e.target as HTMLImageElement;
                        target.src =
                          student.profile.gender?.toLowerCase() === "female"
                            ? "/assets/img/profiles/default-female.png"
                            : "/assets/img/profiles/default-male.png";
                      }}
                    />
                  ) : (
                    <ImageWithBasePath
                      src={
                        student?.profile?.gender?.toLowerCase() === "female"
                          ? "assets/img/profiles/default-female.png"
                          : "assets/img/profiles/default-male.png"
                      }
                      alt="Student"
                    />
                  )}
                </span>
                <div className="name-info">
                  <h6>
                    {student?.first_name}{student?.last_name ? " " + student?.last_name : ""}{" "}
                    <span>{student?.course?.name}</span>
                  </h6>
                </div>
              </div>
              <div className="table-responsive custom-table no-datatable_length">
                <table className="table datanew">
                  <thead className="thead-light">
                    <tr>
                      <th>User Type</th>
                      <th>User Name</th>
                      <th>Password</th>
                    </tr>
                  </thead>
                  <tbody>
                    {student?.user && (
                      <tr>
                        <td>Student</td>
                        <td>{student.user.username || "N/A"}</td>
                        <td>{student.user.password || "N/A"}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <Link
                to="#"
                className="btn btn-light me-2"
                data-bs-dismiss="modal"
              >
                Close
              </Link>
            </div>
          </div>
        </div>
      </div>
      {/* /Login Details */}

      {/* Document Upload Modal */}
      <div className="modal fade" id="upload_modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form>
              <div className="modal-body text-center">
                <span className="upload-icon">
                  <i className="ti ti-file-upload" />
                </span>
                <h4>Upload Documents</h4>
                <p>Would you like to upload documents for this student now?</p>
                <div className="d-flex justify-content-center">
                  <Link
                    to="#"
                    className="btn btn-light me-3"
                    onClick={handleSubmit}
                    data-bs-dismiss="modal"
                  >
                    Skip for Now
                  </Link>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleContinue}
                    data-bs-dismiss="modal"
                  >
                    Yes, Upload
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentModals;
